import { Cloud, CloudOff } from 'lucide-react';
import { useWebSocket } from '@hooks/useWebSocket';
import { NotificationCenter } from '@components/NotificationCenter';
import { useRegisterPageHeaderMeta } from '@components/layouts/pageHeaderMetaRegistry';

/**
 * Ivory & Brass page utility cluster.
 *
 * The global 52px top bar carried exactly two things — connection state and the
 * notification bell — and nothing else, on every route. That is 52px of chrome
 * for two glyphs. The bar is gone (see MainLayout); this cluster goes at the
 * RIGHT END of each page's own header instead, after the page's action buttons.
 *
 * Usage — ticket detail (inside TicketDetailHeader's `actions` slot):
 *   actions={<>{...buttons}<PageHeaderMeta /></>}
 *
 * Usage — tickets list / dashboard / any page with its own toolbar:
 *   <PageHeaderMeta />   // put it last in the toolbar's right group
 *
 * There is no menu button: the nav rail is always on screen (the glyph rail
 * below 1200), so there is no drawer to open.
 */
export function PageHeaderMeta({
  className = '',
  isFallback = false,
}: {
  className?: string;
  /** Set only by MainLayout's fallback, which must not count itself. */
  isFallback?: boolean;
}) {
  const { isConnected } = useWebSocket();
  useRegisterPageHeaderMeta(!isFallback);

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Connection state — a rounded pill with an icon + word. The state is
          always a word, so colour never carries it alone. */}
      <div
        className="flex h-[34px] flex-none items-center gap-1.5 rounded-[10px] border px-3"
        title={isConnected ? 'Real-time updates active' : 'Reconnecting…'}
        style={
          isConnected
            ? {
                borderColor: 'var(--co-green-line)',
                background: 'var(--co-green-wash)',
                color: 'var(--co-green)',
              }
            : {
                borderColor: 'var(--co-terracotta-line)',
                background: 'var(--co-terracotta-wash)',
                color: 'var(--co-terracotta)',
              }
        }
      >
        {isConnected ? (
          <Cloud aria-hidden="true" className="h-3.5 w-3.5 flex-none" />
        ) : (
          <CloudOff aria-hidden="true" className="h-3.5 w-3.5 flex-none" />
        )}
        <span className="font-mono text-[10px] font-semibold uppercase tracking-[.14em]">
          {isConnected ? 'Live' : 'Offline'}
        </span>
      </div>

      <NotificationCenter />
    </div>
  );
}

export default PageHeaderMeta;
